// src/pages/settings/Partnerships.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Box,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Snackbar,
    Tooltip,
    Slide,
    Avatar,
    Card,
    CardContent,
    CardHeader
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Search,
    Refresh,
    Close as CloseIcon,
    Person,
    Email,
    Phone,
    Message as MessageIcon,
    People,
    CheckCircle,
    Cancel,
    Visibility,
    Reply as ReplyIcon,
    Archive as ArchiveIcon,
    MarkEmailRead as MarkReadIcon,
    AccessTime,
    Drafts,
    Inbox,
    ChatBubbleOutlineOutlined,
    Business,
    LocationOn,
    Category,
    TrendingUp
} from '@mui/icons-material';
import { authService } from '../../apiServices/authService';
import { BASE_URL } from '../../apiServices/APIs';
import { recordsCount, recordsPerPage } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';

// API Endpoints
const APIPartnershipList = '/api/partnership/list';
const APIPartnershipShow = '/api/partnership/show';
const APIPartnershipReply = '/api/partnership/reply';
const APIPartnershipStatus = '/api/partnership/status';
const APIPartnershipDelete = '/api/partnership/delete';
const APIPartnershipStats = '/api/partnership/stats';

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Partnerships = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [partnerships, setPartnerships] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // View message dialog
    const [viewDialog, setViewDialog] = useState(false);
    const [viewingPartnership, setViewingPartnership] = useState(null);
    
    // Reply dialog
    const [replyDialog, setReplyDialog] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyErrors, setReplyErrors] = useState({});
    const [sendingReply, setSendingReply] = useState(false);
    
    // Delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Stats
    const [stats, setStats] = useState({
        total_requests: 0,
        unread_requests: 0,
        pending_requests: 0,
        replied_requests: 0,
        archived_requests: 0,
        partner_type_stats: []
    });

    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        fetchPartnerships();
        fetchStats();
    }, []);

    const showMessage = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchPartnerships = async () => {
        setLoading(true);
        setError(null);
        try {
            const api = authService.getApi();
            const response = await api.get(APIPartnershipList);
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                const partnershipsData = Array.isArray(responseData.data) ? responseData.data : [];
                setPartnerships(partnershipsData);
            } else {
                setError(responseData.message);
                showMessage(responseData.message || 'Failed to fetch partnership requests', 'error');
            }
        } catch (error) {
            console.error('Error fetching partnerships:', error);
            setError("Failed to load data");
            showMessage('Error fetching partnership requests', 'error');
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchStats = async () => {
        try {
            const api = authService.getApi();
            const response = await api.get(APIPartnershipStats);
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                const statsData = responseData.data;
                setStats({
                    total_requests: statsData.total_requests || 0,
                    unread_requests: statsData.unread_requests || 0,
                    pending_requests: statsData.pending_requests || 0,
                    replied_requests: statsData.replied_requests || 0,
                    archived_requests: statsData.archived_requests || 0,
                    partner_type_stats: statsData.partner_type_stats || []
                });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    // View partnership message
    const handleViewPartnership = async (partnership) => {
        try {
            const api = authService.getApi();
            const response = await api.get(`${APIPartnershipShow}/${partnership.id}`);
            const responseData = response.data;

            if (responseData.status === 'success') {
                setViewingPartnership(responseData.data);
                setViewDialog(true);
                // Refresh list to update read status
                fetchPartnerships();
            } else {
                showMessage(responseData.message || 'Failed to fetch partnership request', 'error');
            }
        } catch (error) {
            console.error('Error fetching partnership:', error);
            showMessage('Error fetching partnership details', 'error');
        }
    };

    // Open reply dialog
    const handleOpenReply = (partnership) => {
        setViewingPartnership(partnership);
        setReplyMessage('');
        setReplyErrors({});
        setReplyDialog(true);
    };

    // Close reply dialog
    const handleCloseReply = () => {
        setReplyDialog(false);
        setViewingPartnership(null);
        setReplyMessage('');
        setReplyErrors({});
    };

    // Handle reply input change
    const handleReplyChange = (e) => {
        setReplyMessage(e.target.value);
        if (replyErrors.reply_message) {
            setReplyErrors({});
        }
    };

    // Validate reply form
    const validateReply = () => {
        const errors = {};
        
        if (!replyMessage.trim()) {
            errors.reply_message = 'Reply message is required';
        } else if (replyMessage.length < 10) {
            errors.reply_message = 'Reply must be at least 10 characters';
        }
        
        setReplyErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Send reply
    const handleSendReply = async () => {
        if (!validateReply()) return;

        setSendingReply(true);
        try {
            const api = authService.getApi();
            const user = authService.getCurrentUser();
            
            const response = await api.post(APIPartnershipReply, {
                partnership_id: viewingPartnership.id,
                reply_message: replyMessage,
                user_id: user?.id
            });
            
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                showMessage('Reply sent successfully');
                handleCloseReply();
                fetchPartnerships();
                fetchStats();
            } else {
                showMessage(responseData.message || 'Failed to send reply', 'error');
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            showMessage('Error sending reply', 'error');
        } finally {
            if (isMounted.current) {
                setSendingReply(false);
            }
        }
    };

    // Update partnership status
    const handleUpdateStatus = async (partnership, newStatus) => {
        try {
            const api = authService.getApi();
            const response = await api.post(APIPartnershipStatus, {
                partnership_id: partnership.id,
                status: newStatus
            });
            
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                showMessage(`Request marked as ${newStatus}`);
                fetchPartnerships();
                fetchStats();
            } else {
                showMessage(responseData.message || 'Failed to update status', 'error');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            showMessage('Error updating status', 'error');
        }
    };

    // Delete partnership
    const handleDelete = (partnership) => {
        setDeleteConfirm(partnership);
    };

    const confirmDelete = async () => {
        if (deleteConfirm) {
            try {
                const api = authService.getApi();
                const response = await api.post(APIPartnershipDelete, {
                    partnership_id: deleteConfirm.id
                });
                
                const responseData = response.data;

                if (responseData.status === 'success' && isMounted.current) {
                    showMessage('Partnership request deleted successfully');
                    fetchPartnerships();
                    fetchStats();
                    setDeleteConfirm(null);
                } else {
                    showMessage(responseData.message || 'Failed to delete partnership request', 'error');
                }
            } catch (error) {
                console.error('Error deleting partnership:', error);
                showMessage('Error deleting partnership request', 'error');
            }
        }
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setStatusFilter('');
        fetchPartnerships();
        fetchStats();
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'read':
                return 'info';
            case 'replied':
                return 'success';
            case 'archived':
                return 'default';
            default:
                return 'default';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <Inbox fontSize="small" />;
            case 'read':
                return <Drafts fontSize="small" />;
            case 'replied':
                return <ChatBubbleOutlineOutlined fontSize="small" />;
            case 'archived':
                return <ArchiveIcon fontSize="small" />;
            default:
                return <Inbox fontSize="small" />;
        }
    };

    const filteredPartnerships = partnerships.filter(partnership => {
        const matchesSearch = searchTerm === '' ||
            partnership.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            partnership.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            partnership.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            partnership.partner_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            partnership.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            partnership.message_preview?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = !statusFilter || partnership.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    const paginatedPartnerships = filteredPartnerships.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
        return (
            <LoadingState
                message="Loading Partnership Requests..."
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchPartnerships}
                title="Unable to Load Partnership Requests"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Box>
            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People sx={{ color: '#8B4513' }} />
                        Partnership Requests ({recordsCount(partnerships.length)} Total)
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        sx={{ borderColor: '#8B4513', color: '#8B4513' }}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Stats Chips */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                        icon={<People />}
                        label={`Total: ${stats.total_requests}`} 
                        color="primary" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<Inbox />}
                        label={`Pending: ${stats.pending_requests}`} 
                        color="warning" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<Drafts />}
                        label={`Unread: ${stats.unread_requests}`} 
                        color="info" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<ChatBubbleOutlineOutlined />}
                        label={`Replied: ${stats.replied_requests}`} 
                        color="success" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<ArchiveIcon />}
                        label={`Archived: ${stats.archived_requests}`} 
                        color="default" 
                        variant="outlined"
                    />
                </Box>

                {/* Partner Type Breakdown */}
                {stats.partner_type_stats && stats.partner_type_stats.length > 0 && (
                    <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {stats.partner_type_stats.map((stat, index) => (
                            <Chip
                                key={index}
                                label={`${stat.partner_type.split('(')[0].trim()}: ${stat.count}`}
                                size="small"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )}

                {/* Search and Filter */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search by name, organization, email, type or location..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flex: 1, minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            label="Status"
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="read">Read</MenuItem>
                            <MenuItem value="replied">Replied</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>#</TableCell>
                                <TableCell>Contact / Organization</TableCell>
                                <TableCell>Partner Type</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedPartnerships.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            No partnership requests found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedPartnerships.map((partnership, index) => (
                                    <TableRow 
                                        key={partnership.id} 
                                        hover
                                        sx={{
                                            backgroundColor: !partnership.is_read ? '#fdf8f5' : 'inherit',
                                            fontWeight: !partnership.is_read ? 'bold' : 'normal'
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#8B4513', fontSize: 12 }}>
                                                        {partnership.full_name?.charAt(0)?.toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ fontWeight: !partnership.is_read ? 'bold' : 'medium' }}>
                                                        {partnership.full_name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4 }}>
                                                    <Business fontSize="small" color="action" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {partnership.organization}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4 }}>
                                                    <Email fontSize="small" color="action" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {partnership.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={partnership.partner_type_label || partnership.partner_type}
                                                size="small"
                                                variant="outlined"
                                                sx={{ maxWidth: 200 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <LocationOn fontSize="small" color="action" />
                                                <Typography variant="body2">{partnership.location}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 180, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {partnership.message_preview}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(partnership.status)}
                                                label={partnership.status_text || partnership.status}
                                                color={getStatusColor(partnership.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    <AccessTime sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                                    {partnership.created_at_human || partnership.created_at}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Request">
                                                <IconButton 
                                                    onClick={() => handleViewPartnership(partnership)} 
                                                    sx={{ color: '#8B4513' }}
                                                    size="small"
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            </Tooltip>
                                            {partnership.status !== 'replied' && (
                                                <Tooltip title="Reply">
                                                    <IconButton 
                                                        onClick={() => handleOpenReply(partnership)} 
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <ReplyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {partnership.status === 'pending' && (
                                                <Tooltip title="Mark as Read">
                                                    <IconButton 
                                                        onClick={() => handleUpdateStatus(partnership, 'read')} 
                                                        color="info"
                                                        size="small"
                                                    >
                                                        <MarkReadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {partnership.status !== 'archived' && (
                                                <Tooltip title="Archive">
                                                    <IconButton 
                                                        onClick={() => handleUpdateStatus(partnership, 'archived')} 
                                                        color="default"
                                                        size="small"
                                                    >
                                                        <ArchiveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    onClick={() => handleDelete(partnership)} 
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={filteredPartnerships.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* View Partnership Dialog */}
            <Dialog
                open={viewDialog}
                onClose={() => setViewDialog(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', color: '#8B4513' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <People sx={{ color: '#8B4513' }} />
                        <b>Partnership Request Details</b>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {viewingPartnership && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Avatar sx={{ bgcolor: '#8B4513', width: 48, height: 48 }}>
                                                {viewingPartnership.full_name?.charAt(0)?.toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                    {viewingPartnership.full_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <Business fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                                    {viewingPartnership.organization}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <Email fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                                    {viewingPartnership.email}
                                                </Typography>
                                                {viewingPartnership.mobile && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <Phone fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                                        {viewingPartnership.mobile}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                            <Chip 
                                                label={viewingPartnership.partner_type_label || viewingPartnership.partner_type}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Chip 
                                                label={viewingPartnership.location}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Chip 
                                                label={viewingPartnership.status_text} 
                                                color={getStatusColor(viewingPartnership.status)} 
                                                size="small" 
                                            />
                                            <Chip 
                                                label={viewingPartnership.created_at_human} 
                                                variant="outlined" 
                                                size="small" 
                                            />
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Collaboration Objectives / Project Summary:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdf8f5', minHeight: 100 }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {viewingPartnership.message}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                {viewingPartnership.admin_reply && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#4caf50' }}>
                                            Admin Reply:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f9f0', borderColor: '#c8e6c9' }}>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {viewingPartnership.admin_reply}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Replied by: {viewingPartnership.replied_by} • {viewingPartnership.replied_at}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialog(false)} variant="contained" color="error">
                        Close
                    </Button>
                    {viewingPartnership?.status !== 'replied' && (
                        <Button 
                            onClick={() => {
                                setViewDialog(false);
                                handleOpenReply(viewingPartnership);
                            }} 
                            variant="contained"
                            startIcon={<ReplyIcon />}
                            sx={{
                                background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #6B3410 0%, #8B4513 100%)',
                                }
                            }}
                        >
                            Reply
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Reply Dialog */}
            <Dialog
                open={replyDialog}
                onClose={handleCloseReply}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', color: '#8B4513' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <ReplyIcon sx={{ color: '#8B4513' }} />
                        <b>Reply to Partnership Request</b>
                    </Box>
                    {viewingPartnership && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            To: {viewingPartnership.full_name} ({viewingPartnership.organization})
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {viewingPartnership && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa', maxHeight: 150, overflow: 'auto' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Original Request:
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {viewingPartnership.message_preview || viewingPartnership.message}
                                </Typography>
                            </Paper>
                        )}
                        <TextField
                            fullWidth
                            label="Your Reply *"
                            name="reply_message"
                            value={replyMessage}
                            onChange={handleReplyChange}
                            required
                            multiline
                            rows={6}
                            error={!!replyErrors.reply_message}
                            helperText={replyErrors.reply_message}
                            placeholder="Type your reply here... (minimum 10 characters)"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseReply} 
                        variant="contained" 
                        color="error"
                        disabled={sendingReply}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSendReply} 
                        variant="contained"
                        disabled={sendingReply}
                        startIcon={sendingReply ? null : <ReplyIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6B3410 0%, #8B4513 100%)',
                            }
                        }}
                    >
                        {sendingReply ? 'Sending...' : 'Send Reply'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', color: '#8B4513' }}>Confirm Delete Request</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the partnership request from <strong>"{deleteConfirm?.organization}"</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Contact: {deleteConfirm?.full_name} ({deleteConfirm?.email})
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <strong>Warning:</strong> This action cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)} variant="contained" color="error">Cancel</Button>
                    <Button 
                        onClick={confirmDelete} 
                        variant="contained" 
                        startIcon={<DeleteIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6B3410 0%, #8B4513 100%)',
                            }
                        }}
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Partnerships;